import React, { useRef, useState, ChangeEvent } from 'react';
import { AlertCircle, ImageIcon, Loader, Upload, X } from 'lucide-react';
import type { FormData } from '../../../utils/voucherCommon';
import { resolveImageContentType } from '../../../utils/storage';

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const MIN_IMAGE_DIMENSION = 600;
const IMAGE_INPUT_ID = 'voucher-preview-image-input';

export interface VoucherPreviewImageValidated {
  file: File;
  dataUrl: string;
}

export interface VoucherPreviewCardProps {
  title?: string;
  benefitText?: string;
  validityText?: string;
  /** 표시 이미지 (부모가 관리: 기존 URL / S3 완료 후 dataUrl) */
  imageUrl?: string | null;
  isValidating?: boolean;
  isUploading?: boolean;
  error?: string;
  /** 검증 통과 후 — 부모에서 presign+S3 업로드 */
  onValidatedImage?: (next: VoucherPreviewImageValidated) => void;
  /** 삭제 버튼 — 부모에서 staged delete / temp 초기화 */
  onRemoveImage?: () => void;
  onImageError?: (message: string) => void;
  /** 표시 URL 로드 실패 (깨진 이미지) */
  onImageLoadError?: () => void;
}

/** datetime-local / ISO → `YYYY.MM.DD` */
const formatDateDots = (raw: string): string | null => {
  const datePart = raw.trim().split('T')[0]?.split(' ')[0];
  if (!datePart) return null;
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return null;
  return `${y}.${m}.${d}`;
};

export const formatVoucherPreviewBenefit = (formData: FormData): string => {
  if (formData.type === 'COUPON' || formData.type === 'EVENT') {
    if (formData.discountType === 'PERCENTAGE') {
      const max =
        formData.maxDiscount > 0
          ? `(최대 ${formData.maxDiscount.toLocaleString()}원)`
          : '';
      return `${formData.discountValue}% 할인${max ? ` ${max}` : ''}`.trim();
    }
    return `${(formData.discountValue ?? 0).toLocaleString()} 원 할인`;
  }

  if (formData.price > 0) {
    return `${formData.price.toLocaleString()} 원`;
  }

  if (formData.balance > 0) {
    return `${formData.balance.toLocaleString()} 원`;
  }

  return '혜택 미설정';
};

export const formatVoucherPreviewValidity = (formData: FormData): string => {
  if (Number(formData.validType) === 1) {
    const until = String(formData.validUntil || '').trim();
    if (!until) return '유효기간 미설정';
    const dots = formatDateDots(until);
    return dots ? `유효기간 ~${dots}` : `유효기간 ~${until}`;
  }

  if (formData.validPeriod > 0) {
    return `유효기간 발급일로부터 ${formData.validPeriod}일`;
  }

  return '유효기간 미설정';
};

/**
 * 교환권 생성/수정 화면용 가로형 미리보기 카드.
 * 우측 이미지: 클릭·드래그 → 검증 → onValidatedImage (부모에서 S3).
 */
const VoucherPreviewCard: React.FC<VoucherPreviewCardProps> = ({
  title,
  benefitText,
  validityText,
  imageUrl,
  isValidating = false,
  isUploading = false,
  error,
  onValidatedImage,
  onRemoveImage,
  onImageError,
  onImageLoadError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState<string>('');
  const [internalValidating, setInternalValidating] = useState(false);
  const [isBroken, setIsBroken] = useState(false);

  const displayUrl =
    imageUrl && !isBroken && !(isValidating || isUploading || internalValidating)
      ? imageUrl
      : null;
  const displayError = error || internalError;
  const busy = isValidating || isUploading || internalValidating;

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fail = (message: string) => {
    setInternalError(message);
    onImageError?.(message);
    setInternalValidating(false);
    resetInput();
  };

  const processFile = (file: File) => {
    const contentType = resolveImageContentType(file);
    if (!contentType) {
      fail('PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다');
      return;
    }

    if (file.size >= MAX_IMAGE_SIZE_BYTES) {
      fail('파일 크기는 10MB 미만이어야 합니다');
      return;
    }

    setIsBroken(false);
    setInternalError('');
    setInternalValidating(true);

    const img = document.createElement('img');
    const reader = new FileReader();
    let isValidated = false;

    const validateDimensions = (dataUrl: string) => {
      if (isValidated) return;
      isValidated = true;

      if (img.naturalWidth < MIN_IMAGE_DIMENSION || img.naturalHeight < MIN_IMAGE_DIMENSION) {
        fail('이미지 크기는 600x600 픽셀 이상이어야 합니다');
        return;
      }

      setInternalValidating(false);
      setInternalError('');
      onValidatedImage?.({ file, dataUrl });
    };

    reader.onerror = () => fail('이미지를 읽을 수 없습니다');

    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (typeof dataUrl !== 'string') {
        fail('이미지를 읽을 수 없습니다');
        return;
      }

      img.onload = () => validateDimensions(dataUrl);
      img.onerror = () => fail('이미지를 불러올 수 없습니다');
      img.src = dataUrl;

      if (img.complete && img.naturalWidth > 0) {
        validateDimensions(dataUrl);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setInternalError('');
    setIsBroken(false);
    resetInput();
    onRemoveImage?.();
  };

  // 부모 imageUrl이 바뀌면 broken 상태 초기화
  React.useEffect(() => {
    setIsBroken(false);
  }, [imageUrl]);

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-4 sm:gap-6">
        <div className="flex-1 min-w-0 flex flex-col gap-2 sm:gap-2.5">
          <p className="text-xs sm:text-sm text-gray-500 truncate">브랜드명/매장명</p>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug whitespace-pre-wrap break-words">
            {title?.trim() || '교환권 명칭'}
          </h3>
          <p className="text-base sm:text-lg font-bold text-gray-900">
            {benefitText || '혜택 미설정'}
          </p>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">
            {validityText || '유효기간 미설정'}
          </p>
        </div>

        <div
          className="flex-shrink-0 relative"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            if (busy) return;
            const file = e.dataTransfer.files?.[0];
            if (!file) return;
            processFile(file);
          }}
        >
          {!displayUrl || busy ? (
            <label
              htmlFor={IMAGE_INPUT_ID}
              className={`flex flex-col items-center justify-center w-28 h-28 sm:w-32 sm:h-32 rounded-3xl border-2 border-dashed cursor-pointer transition-colors ${
                displayError
                  ? 'border-red-300 bg-red-50'
                  : isDragging
                    ? 'border-gray-500 bg-gray-100'
                    : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              } ${busy ? 'pointer-events-none opacity-70' : ''}`}
            >
              {isUploading ? (
                <>
                  <Loader className="w-7 h-7 text-gray-400 animate-spin mb-1" />
                  <span className="text-[10px] sm:text-xs text-gray-500 text-center px-1">
                    업로드 중...
                  </span>
                </>
              ) : busy ? (
                <>
                  <Loader className="w-7 h-7 text-gray-400 animate-spin mb-1" />
                  <span className="text-[10px] sm:text-xs text-gray-500 text-center px-1">
                    확인 중...
                  </span>
                </>
              ) : (
                <>
                  <Upload className="w-7 h-7 text-gray-400 mb-1" />
                  <span className="text-[10px] sm:text-xs text-gray-500 text-center leading-tight px-1">
                    클릭 또는 드롭
                  </span>
                  <span className="text-[9px] text-gray-400 text-center leading-tight px-1 mt-0.5">
                    600×600↑ · 10MB↓
                  </span>
                </>
              )}
            </label>
          ) : (
            <div
              className={`relative w-28 h-28 sm:w-32 sm:h-32 ${
                isDragging ? 'ring-2 ring-gray-400 rounded-3xl' : ''
              }`}
            >
              <label
                htmlFor={IMAGE_INPUT_ID}
                className="block w-full h-full rounded-3xl overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer"
                title="다른 이미지 선택"
              >
                <img
                  src={displayUrl}
                  alt="교환권 이미지 미리보기"
                  className="w-full h-full object-cover"
                  onError={() => {
                    setIsBroken(true);
                    onImageLoadError?.();
                  }}
                />
              </label>
              <button
                type="button"
                onClick={handleRemove}
                className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
                aria-label="이미지 삭제"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <input
            id={IMAGE_INPUT_ID}
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
            onChange={handleInputChange}
            className="hidden"
            disabled={busy}
          />
        </div>
      </div>

      {displayError && (
        <p className="mt-3 text-sm text-red-500 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
          {displayError}
        </p>
      )}

      {!displayUrl && !displayError && (
        <p className="mt-3 text-xs text-gray-400 flex items-center sm:hidden">
          <ImageIcon className="w-3.5 h-3.5 mr-1" />
          PNG, JPG, JPEG, WEBP · 최소 600×600 · 10MB 미만
        </p>
      )}
    </div>
  );
};

export default VoucherPreviewCard;
